import {
  forwardRef,
  useCallback,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { cx } from '../utils/cx.js';
import {
  useProximityHover,
  type ProximityAxis,
  type UseProximityHoverOptions,
} from '../hooks/useProximityHover.js';
import styles from './ProximityHighlight.module.css';

export type ProximityHighlightProps = {
  style?: CSSProperties;
  className?: string;
  /** When true, highlight is considered lit (opacity class). */
  active?: boolean;
};

/** Shared wash layer — place as first child of a `position: relative` proximity scope. */
export function ProximityHighlight({ style, className, active = false }: ProximityHighlightProps) {
  return (
    <span
      className={cx(styles.highlight, active && styles.highlightVisible, className)}
      style={style}
      aria-hidden
      data-wp-proximity-highlight=""
    />
  );
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else ref.current = value;
}

export type ProximityScopeProps = {
  children: ReactNode;
  axis?: ProximityAxis;
  enabled?: boolean;
  itemSelector?: string;
  className?: string;
  /** Polymorphic root element. Default `div`. */
  as?: ElementType;
} & Omit<HTMLAttributes<HTMLElement>, 'children' | 'className'>;

/**
 * Relative container + shared proximity highlight.
 * Mark interactive children with `data-wp-proximity-item`.
 */
export const ProximityScope = forwardRef<HTMLElement, ProximityScopeProps>(function ProximityScope(
  {
    children,
    axis = 'xy',
    enabled = true,
    itemSelector,
    className,
    as: Comp = 'div',
    ...rest
  },
  forwardedRef,
) {
  const opts: UseProximityHoverOptions = { axis, enabled, itemSelector };
  const { containerRef, containerProps, highlightStyle, active } = useProximityHover(opts);

  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      (containerRef as { current: HTMLElement | null }).current = node;
      assignRef(forwardedRef, node);
    },
    [containerRef, forwardedRef],
  );

  return (
    <Comp
      ref={setRefs}
      className={cx(styles.scope, className)}
      {...rest}
      {...containerProps}
    >
      <ProximityHighlight style={highlightStyle} active={active} />
      {children}
    </Comp>
  );
});
